'use client';

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';

export const EVERGREEN_LINKS = [
  {
    href: '/explore',
    label: 'Browse All Investment Opportunities',
    description: 'Discover startups and growth-stage companies raising capital on StartEngine.',
  },
  {
    href: '/explore?filter=private',
    label: 'Pre-IPO Funds',
    description: 'Explore Pre-IPO fund offerings on StartEngine.',
  },
  {
    href: '/explore?filter=crowdfunding',
    label: 'Browse Startups',
    description: 'Explore startup offerings on StartEngine.',
  },
];

export const PRIMARY_EVERGREEN_HREF = '/explore';

function selectLinks(slug, count = 3) {
  let seed = (slug || '').split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const next = () => {
    seed = (seed * 1664525 + 1013904223) & 0xffffffff;
    return (seed >>> 0) / 0x100000000;
  };
  const primary = EVERGREEN_LINKS.find((link) => link.href === PRIMARY_EVERGREEN_HREF);
  const rest = EVERGREEN_LINKS.filter((link) => link.href !== PRIMARY_EVERGREEN_HREF);
  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  const ordered = primary ? [primary, ...rest] : rest;
  return ordered.slice(0, count);
}

const EvergreenLinks = ({ slug }) => {
  const links = useMemo(() => selectLinks(slug, 3), [slug]);

  return (
    <Box
      component="nav"
      data-cy="evergreen-links"
      aria-label="Learn more about investing"
      sx={{
        mt: 4,
        mb: 2,
        p: 3,
        borderRadius: 2,
        backgroundColor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Typography variant="h6" component="h2" sx={{ mb: 2, fontWeight: 600, fontSize: '1.1rem' }}>
        Learn More About Investing
      </Typography>
      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {links.map((link) => (
          <Box component="li" key={link.href} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
            <Link href={link.href} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Typography
                component="span"
                sx={{
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {link.label}
              </Typography>
            </Link>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.85rem' }}
            >
              {link.description}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

EvergreenLinks.propTypes = {
  slug: PropTypes.string,
};

export default EvergreenLinks;
